import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Global service that resolves and exposes the current clientId.
 * Reads from route data (e.g. /registration/celcomdigi) or query param (?clientId=xxx).
 * Any component can inject this to get the current clientId without parsing the URL.
 */
@Injectable({ providedIn: 'root' })
export class ClientService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);

  private readonly _clientId = new BehaviorSubject<string>(environment.AUTH_CLIENT_ID);

  /** Current clientId as observable */
  readonly clientId$ = this._clientId.asObservable();

  /** Current clientId value (synchronous) */
  get clientId(): string {
    return this._clientId.value;
  }

  // --- Landing state (additive) ---
  private readonly _isLanding = new BehaviorSubject<boolean>(false);

  /** Whether the current context is a landing form */
  readonly isLanding$ = this._isLanding.asObservable();

  /** Current isLanding value (synchronous) */
  get isLanding(): boolean {
    return this._isLanding.value;
  }

  /** Set the landing state */
  setLanding(value: boolean): void {
    this._isLanding.next(value);
  }

  constructor() {
    // Resolve on every navigation end
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.resolveClientId();
      });

    // Also try to resolve immediately if route tree is already available (browser)
    if (isPlatformBrowser(this.platformId)) {
      // Use setTimeout to let the route tree initialize
      setTimeout(() => this.resolveClientId(), 0);
    }
  }

  private resolveClientId(): void {
    try {
      // Walk the activated route tree to find the deepest route with data or queryParams
      let currentRoute = this.route.root;
      while (currentRoute.firstChild) {
        currentRoute = currentRoute.firstChild;
      }

      const snapshot = currentRoute.snapshot;
      if (!snapshot) return;

      const dataClientId = snapshot.data?.['clientId'];
      const queryClientId = snapshot.queryParamMap?.get('clientId')
        || this.route.snapshot?.queryParamMap?.get('clientId');

      let resolved = environment.AUTH_CLIENT_ID;
      if (queryClientId) {
        resolved = queryClientId.toLowerCase();
      } else if (dataClientId) {
        resolved = dataClientId.toLowerCase();
      }

      if (resolved !== this._clientId.value) {
        this._clientId.next(resolved);
      }
    } catch {
      // Silently ignore during SSR when route tree isn't ready
    }
  }
}
